// ============================================================================
// Beacon — long-horizon projection engine.
//
// Pure & deterministic, like the underwriting engine. Given a stabilized deal,
// it walks year-by-year (1..30) and decomposes how WEALTH is built:
//
//   equity = initial equity
//          + loan paydown        (amortization — the tenant paying your mortgage)
//          + market appreciation (natural rent/expense growth ÷ cap rate)
//          + forced value-add    (your $/unit rent bump ÷ cap rate)
//
// The decomposition reconciles exactly to (value − loan balance), and drives
// the refi "snowball": how much equity you can pull out to fund the next deal.
// ============================================================================

import type { Deal, Metrics } from "../types";
import { mortgageConstant, loanBalance } from "../engine/engine";

export interface YearRow {
  year: number;
  gpr: number;
  noi: number;
  value: number;        // stabilized value at market cap = NOI / exitCap
  loanBalance: number;
  principalPaid: number; // cumulative L0 − balance
  equity: number;        // value − loan balance
  cashFlow: number;      // NOI − annual debt service
  cumCashFlow: number;
  // equity decomposition (sums to `equity`)
  eqInitial: number;
  eqPaydown: number;
  eqAppreciation: number;
  eqValueAdd: number;
}

export interface Projection {
  horizon: number;
  rentBumpPerUnit: number; // $/unit/month, applied as a permanent lift from year 1
  units: number;
  v0: number;              // value at stabilization (year 0)
  l0: number;              // starting loan balance
  e0: number;              // starting equity
  debtSvc: number;         // fixed annual debt service
  refiLTV: number;         // LTV a lender will refinance to
  rows: YearRow[];
  end: YearRow;            // rows[horizon-1]
  maxLoanAtEnd: number;    // refiLTV × value at horizon
  cashOutAtEnd: number;    // max(0, maxLoan − balance) — pullable for the next deal
}

/** NOI at year y given an annual gross-rent base, using the engine's expense model. */
function noiAt(d: Deal, m: Metrics, grossBase: number, y: number): number {
  const grossY = grossBase * Math.pow(1 + d.rentGrow, y);
  const egi = grossY * (1 - d.vacPct) + d.otherInc;
  // fixed opex grows with inflation; management is a % of EGI (recomputed)
  const ex = (m.opex - m.mgmt) * Math.pow(1 + d.expGrow, y) + egi * d.mgmtPct;
  return egi - ex;
}

export function project(d: Deal, m: Metrics, horizon: number, rentBumpPerUnit = 0): Projection {
  const units = d.u2c + d.u1c;
  const l0 = m.permLoan;
  const v0 = m.stabVal;
  const e0 = v0 - l0;
  const debtSvc = m.debtSvc;
  const refiLTV = d.targetLTV;

  const grossBase = m.gpr; // annual gross potential rent at stabilization
  const grossVA = grossBase + rentBumpPerUnit * units * 12; // permanent value-add lift

  const rows: YearRow[] = [];
  let cum = 0;
  for (let y = 1; y <= horizon; y++) {
    const noiBase = noiAt(d, m, grossBase, y);
    const noiVA = noiAt(d, m, grossVA, y);
    const valueBase = noiBase / d.exitCap;
    const value = noiVA / d.exitCap; // the simulated (value-add) reality
    const bal = loanBalance(l0, d.permRate, d.amort, y);
    const paid = l0 - bal;
    const cf = noiVA - debtSvc;
    cum += cf;

    rows.push({
      year: y,
      gpr: grossVA * Math.pow(1 + d.rentGrow, y),
      noi: noiVA,
      value,
      loanBalance: bal,
      principalPaid: paid,
      equity: value - bal,
      cashFlow: cf,
      cumCashFlow: cum,
      eqInitial: e0,
      eqPaydown: paid,
      eqAppreciation: valueBase - v0,
      eqValueAdd: value - valueBase,
    });
  }

  const end = rows[rows.length - 1];
  const maxLoanAtEnd = refiLTV * end.value;
  const cashOutAtEnd = Math.max(0, maxLoanAtEnd - end.loanBalance);

  return { horizon, rentBumpPerUnit, units, v0, l0, e0, debtSvc, refiLTV, rows, end, maxLoanAtEnd, cashOutAtEnd };
}

/** Annual amortization split for a given year (principal vs interest paid that year). */
export function amortSplit(d: Deal, m: Metrics, year: number): { principal: number; interest: number } {
  const balPrev = year <= 1 ? m.permLoan : loanBalance(m.permLoan, d.permRate, d.amort, year - 1);
  const balNow = loanBalance(m.permLoan, d.permRate, d.amort, year);
  const principal = balPrev - balNow;
  const pay = m.permLoan * mortgageConstant(d.permRate, d.amort); // level annual payment
  return { principal, interest: Math.max(0, pay - principal) };
}

export const HORIZONS = [1, 5, 20, 30] as const;
